import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "utils/helpers/enums/routes";
import RsvpBreadcrumb, {
  RsvpBreadcrumbLink,
} from "components/breadcrumbs/RsvpBreadcrumb";
import useRsvp from "containers/rsvp/useHooks";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import useUtils from "hooks/useUtils";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import WorkshopSessionPopup from "components/popups/workshop-session";
import { ReviewFilterDTO } from "utils/helpers/models/attendee/review-filter-dto";
import { TiStarFullOutline, TiStarHalfOutline } from "react-icons/ti";
import { useSelector } from "react-redux";
import ExperienceDetailHeader from "components/experience-detail-header";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import noFoundGif from "assets/images/gifs/noFoundGif.gif";

const tabs = ["Reviews", "Cup of Gratitude"];

const RsvpViewRatings = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Reviews");
  const { getQueryParams } = useUtils();
  const [event, setEvent] = useState<EventDTO>(new EventDTO());
  const [data, setData] = useState<any[]>([]);
  const params = getQueryParams();
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const [workshopSessionModal, setWorkshopSessionModal] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const { getEventById, getEventsReviews } = useRsvp();

  const getEventDataByID = async () => {
    if (params?.id) {
      await getEventById(params?.id, setEvent);
    }
  };

  useEffect(() => {
    getEventDataByID();
  }, []);

  const goToAttendess = () => {
    navigate(siteRoutes.rsvpAttendess, {
      state: { eventData: event },
    });
  };
  // hide button on the base of startTime
  const lastSession = event.workshopSessions[event.workshopSessions.length - 1];
  const lastSessionDateTime = new Date(
    `${lastSession.startDateWithMonthAndYear} ${lastSession.endTime}`
  );

  const visibleSessions = event.workshopSessions.slice(0, 3);
  const hasMoreSessions = event.workshopSessions.length > 3;

  const [filters, setFilters] = useState<Partial<ReviewFilterDTO>>({
    page: 1,
    totalPages: 1,
    size: 15,
    eventId: params?.id,
  });
  const getAllReviews = async (filters: Partial<ReviewFilterDTO>) => {
    if (!filters.eventId) return;
    const isFirstPage = filters.page === 1;
    await getEventsReviews(
      setData,
      { ...filters, page: filters.page - 1 },
      isFirstPage ? [] : data,
      setFilters
    );
    setFilters({ ...filters });
  };

  useEffect(() => {
    if (params?.id) {
      getAllReviews({ ...filters, eventId: params.id });
    }
  }, [params.id]);

  const [cupOfGratitudeData, setCupOfGratitudeData] = useState<[]>([]);
  const { getCupGradituteById } = useRsvp();
  useEffect(() => {
    const id = Number(params?.id);
    if (id) {
      getCupGradituteById(id, setCupOfGratitudeData);
    }
  }, []);

  // Mark initial load as complete after first data load
  useEffect(() => {
    if (isInitialLoad && !isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, data, cupOfGratitudeData]);

  const groupedData = Object.values(
    cupOfGratitudeData.reduce((acc: any, item: any) => {
      if (!acc[item.cogId]) {
        acc[item.cogId] = { ...item, totalPrice: item.price, count: 1 };
      } else {
        acc[item.cogId].totalPrice += item.price;
        acc[item.cogId].count += 1;
      }
      return acc;
    }, {})
  );

  const breadcrumbLinks: RsvpBreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.rsvpHome },
    { title: "Reviews", path: siteRoutes.rsvpViewRatings },
  ];

  return (
    <div>
      <ExperienceDetailHeader
        event={event}
        breadcrumb={<RsvpBreadcrumb links={breadcrumbLinks} />}
        // onHostClick={goToHostDetail}
        onSeeMoreSessions={() => setWorkshopSessionModal(true)}
        onAttendeesClick={goToAttendess}
        visibleSessions={visibleSessions}
        hasMoreSessions={hasMoreSessions}
        gradientClass="bg-custom-gradient-rsvp"
      />
      <div className="min-h-[50vh] container ">
        <h2 className="text-2xl font-semibold text-blackie mb-6">
          Reviews & Cup of Gratitude
        </h2>

        <div className="flex space-x-4 mb-4">
          {tabs.map((tab) => (
            <button
              key={tab}
              className={`text-sm font-semibold px-4 py-2 ${
                activeTab === tab
                  ? "border-b-2 border-[#4880FF] text-[#4880FF] tabsBlueBg"
                  : "text-gray-500"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === "Reviews" && (
          <div className="border-[0.3px] border-labelTextColor p-6 rounded-lg shadow-sm flex flex-col gap-10">
            {data?.map((review: any) => (
              <div
                key={review.id}
                className="flex md:flex-nowrap flex-wrap items-start justify-between "
              >
      
                <div className="flex flex-col gap-3 w-full">
            
                  <div className="flex items-center gap-2">
                    <img
                      src={concatPathWithBackendUrl(
                        review?.user?.profilePic?.fileDownloadUri
                      )}
                      alt="Avatar"
                      className="w-10 h-10 rounded-full"
                    />
                    <div>
                      <h4 className="font-semibold text-black text-lg">
                        {review.user.firstName} {review.user.lastName}
                      </h4>
                      <p className="text-sm text-labelTextColor">
                        <p className="text-[10px] text-warmGray">
                          {" "}
                          {review?.createdAt
                            ? new Date(review.createdAt)
                                .toLocaleString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                  hour: "numeric",
                                  minute: "numeric",
                                  hour12: true,
                                })
                                .replace(",", " -")
                            : "N/A"}
                        </p>
                      </p>
                    </div>
                  </div>

                  {/* Review Text */}
                  <div>
                    <p className="text-blackie">{review.comment}</p>
                  </div>
                </div>

                {/* Right Column - Rating and Cup Icon */}
                <div className="flex flex-col gap-2 md:gap-3 md:items-end md:px-6 mt-3">
                  {/* Rating */}
                  <div className="flex items-center space-x-2">
                    {[...Array(5)].map((_, i) => {
                      if (
                        review.rating % 1 !== 0 &&
                        i === Math.floor(review.rating)
                      ) {
                        return (
                          <div key={i} className="relative w-4 h-4">
                            <TiStarFullOutline className="absolute w-4 h-4 text-gray-300" />
                            <TiStarHalfOutline className="absolute w-4 h-4 text-yellow-500" />
                          </div>
                        );
                      }
                      return (
                        <TiStarFullOutline
                          key={i}
                          className={`w-4 h-4 ${
                            i < review.rating
                              ? "text-yellow-500"
                              : "text-gray-300"
                          }`}
                        />
                      );
                    })}
                    <span className="ml-2 text-labelTextColor">
                      ({review.rating})
                    </span>
                  </div>
                </div>
              </div>
            ))}
            <CustomMessageDisplay
              show={!isLoading && !isInitialLoad && !data.length}
              title="No Reviews Available"
              slogan="This event hasn't received any reviews yet."
              image={noFoundGif}
              imageAlt="No reviews found"
              className="py-16"
              imageClassName="w-24 h-24"
              titleClassName="text-2xl font-bold text-blackie"
              sloganClassName="text-gray-500 text-base"
            />

            {filters.totalPages > filters.page && (
              <div className="text-center mt-10">
                <button
                  className="px-6 py-2 bg-fade text-primaryBlueText rounded-full"
                  onClick={() =>
                    getAllReviews({ ...filters, page: filters.page + 1 })
                  }
                >
                  See More
                </button>
              </div>
            )}
          </div>
        )}

        {/* 👇 Show this section only when Milestones tab is active */}
        {activeTab === "Cup of Gratitude" && (
          <div className="">
            <h1 className="text-3xl font-medium text-blackie my-10">
              All ({groupedData?.length || 0})
            </h1>
            {groupedData.length > 0 ? (
              <div className="flex flex-wrap gap-4">
                {groupedData.map((cup: any) => (
                  <div key={cup.cogId} className=" text-center w-[185px]">
                    <div className=" flex justify-center">
                      <img
                        src={concatPathWithBackendUrl(cup.image?.fileDownloadUri)}
                        alt={cup.name}
                        className="h-[150px] flex justify-center"
                      />
                    </div>

                    <span className="text-blackie text-xl font-bold ">
                      ${cup.price}
                    </span>
                    <h3 className="text-blackie text-4xl font-bold mb-2 -mt-3">
                      {cup?.name}
                    </h3>
                    <div className="mx-auto w-fit mt-2">
                      <p className="text-xs bg-yellowBg text-yellowText px-3 py-1 rounded-full">
                        {cup.count} {cup.count > 1 ? "times" : "time"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <CustomMessageDisplay
                show={!isLoading && !isInitialLoad}
                title="No Cups of Gratitude Available"
                slogan="This event hasn't received any cups of gratitude yet. Show your appreciation by sending one!"
                image={noFoundGif}
                imageAlt="No cups of gratitude found"
                className="py-16"
                imageClassName="w-24 h-24"
                titleClassName="text-2xl font-bold text-blackie"
                sloganClassName="text-gray-500 text-base"
              />
            )}
          </div>
        )}
      </div>
      {workshopSessionModal && (
        <WorkshopSessionPopup
          isOpen={workshopSessionModal}
          onClose={() => setWorkshopSessionModal(false)}
          sessions={event.workshopSessions}
        />
      )}
    </div>
  );
};

export default RsvpViewRatings;
